import { Store as StoreIcon } from '@mui/icons-material'
import { observer } from 'mobx-react-lite'
import { Fragment, ReactElement } from 'react'
import { ImageBypartAPIResponse } from 'src/api/searchResults/interfaces'
import { CartItemRow } from 'src/features/cart/components/CartItemRow'
import { OrderTypeTransportRow } from './OrderTypeTransportRow'
import { Vehicle } from 'src/store/models/Vehicles'
import { defaultTheme } from 'src/theme'
import { breakpoints } from 'src/theme/breakpoints'
import styled from 'styled-components'
import { PartsLocations, SellerWithLocations } from '../store/ISellerCart'
import { OrderSelection } from '../../../../src/store/models/ShoppingCartModels'
import { isBuyDirectLocation } from '../Utils'

interface SellerLocationsProps {
  partImages: ImageBypartAPIResponse[]
  sellerDetails: SellerWithLocations
  vehicle: Vehicle
}

export const SellerLocations = observer(
  ({
    partImages,
    sellerDetails,
    vehicle,
  }: SellerLocationsProps): ReactElement => {
    const normalLocations: PartsLocations[] = []
    const buyDirectLocations: PartsLocations[] = []

    sellerDetails.locations?.forEach((loc) => {
      if (isBuyDirectLocation(loc)) {
        buyDirectLocations.push(loc)
      } else {
        normalLocations.push(loc)
      }
    })

    const primaryLocationName = sellerDetails.locations.find(
      (l) => l.locationId.toString() === '100'
    )?.called

    const getOrderTypeTransortRow = (locations: PartsLocations[]) => {
      const isBuyDirect = isBuyDirectLocation(locations[0])
      return (
        <LocationRow>
          <td>
            <LocationName>
              <span>
                <StyledStoreIcon />
                {isBuyDirect
                  ? locations[0]?.called
                  : primaryLocationName || locations[0]?.called}
              </span>
            </LocationName>
          </td>
          {sellerDetails?.coverageKey === '' && (
            <td colSpan={7}>
              <OrderTypeTransportRow vehicle={vehicle} locations={locations} />
            </td>
          )}
        </LocationRow>
      )
    }

    const getCartItemRows = (locations: PartsLocations[]) => {
      return (
        <Fragment key={locations[0].locationId}>
          {getOrderTypeTransortRow(locations)}
          {locations.map((location) => {
            const { locationId } = location
            const productsFromLocation = location.parts

            return (
              <Fragment key={locationId}>
                {productsFromLocation.map((product) => {
                  return (product.bundleInfo || []).map((bundledProduct) => {
                    const orderSelection: OrderSelection = {
                      locationId,
                      quantityRequested: bundledProduct.quantityRequested,
                    }
                    return (
                      <CartItemRow
                        key={
                          (vehicle.id || '').toString() +
                          product.lineNo +
                          product.partNumber +
                          locationId +
                          String(bundledProduct.bundled)
                        }
                        product={product}
                        orderSelection={orderSelection}
                        vehicle={vehicle}
                        partImages={partImages}
                        sellerDetails={sellerDetails}
                        bundledProduct={bundledProduct}
                      />
                    )
                  })
                })}
              </Fragment>
            )
          })}
        </Fragment>
      )
    }

    return (
      <>
        {normalLocations.length > 0 && getCartItemRows(normalLocations)}
        {buyDirectLocations.map((loc) => getCartItemRows([loc]))}
      </>
    )
  }
)

const StyledStoreIcon = styled(StoreIcon)`
  color: #7ebd34;
`

const LocationName = styled.div`
  font-family: Roboto;
  font-size: 16px;
  font-weight: bold;
  text-align: left;
  padding: 12px;
`

export const LocationRow = styled.tr`
  background-color: ${defaultTheme.colors.white};
  color: ${defaultTheme.colors.primaryText};
  @media (max-width: ${breakpoints.mobileLarge}px) {
    display: flex;
    flex-direction: column;
  }
`
